import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {QuestionBase} from '../../domain/question.base';
import {ScrollService} from '../scroll.service';
import {ListQuestionComponent} from "../list-question.component";
import {ApiService} from "../api.service";
import {Datasource} from "../../domain/datasource";
import * as JmesPath from 'jmespath';

@Component({
  selector: `crds-question-dropdown`,
  template: `
    <div [formGroup]="form" *ngIf="optionsPopulated && show">
      <div *ngIf="initialised">
        <label *ngIf="question.label" class="form-label" [attr.for]="question.key">{{question.label}}
          <span *ngIf="question.required"> *</span>
          <crds-form-error *ngIf="isInvalid" [question]="question"
                           [message]="question.errorMessage"></crds-form-error>
        </label>
        <select [id]="question.key"
                [formControlName]="question.key"
                class="form-control mb-2">
          <option *ngFor="let opt of currentFilter.length ? currentFilter : question.options"
                  [ngValue]="opt.value"
                  [selected]="getSelected(opt.value)"
                  #dropDownInput>
            {{opt.key}}
          </option>
        </select>

        <!-- QUESTION HINT -->
        <crds-question-hint [question]="question"></crds-question-hint>

        <p *ngIf="alert">
          <ngb-alert [dismissible]="false" type="secondary">
            <i class="material-icons">info</i> {{question.alert[form.get(question.key).value]}}
          </ngb-alert>
        </p>
      </div>
    </div>`,
})
export class DropdownQuestionComponent extends ListQuestionComponent implements OnInit {
  @Input() form;
  @Input() question: QuestionBase<any>;
  @ViewChild('dropDownInput') input;
  type: string;
  initialised: boolean = false;

  /**
   * The original options which may need to be used as a base
   * to filter if this question is subscribed to the value of another question.
   */
  currentFilter: any[] = [];

  constructor(private scrollService: ScrollService, protected apiService: ApiService) {
    super(apiService);
  }

  async ngOnInit() {

    if (this.question.datasource) {
      if (!this.question.datasource.transform) {
        this.question.datasource.transform = '[].{ text: join(`" - "`, [parent.name, name]), value: join(`" - "`, [parent.name, name]) }';
      }
      await this.getData(this.question.datasource);
    }
    this.initialised = true;

    this.currentFilter = Object.assign([], this.question.options);
    this.scrollService.scrollObservable
      .subscribe((documentId) => {
        if (documentId === this.question.key) {
          this.input.nativeElement.scrollIntoView();
        }
      });
    this.setSubscriptions();
  }

  public mapData(result: any[], datasource: Datasource) {
    let options = [];
    let transformed = JmesPath.search(result, datasource.transform);
    transformed.forEach(item => {
      let initialText = item.text;
      let initialValue = item.value;

      if (typeof initialValue === 'string') {
        options.push({
          key: initialText,
          value: initialValue
        });
      } else if (Array.isArray(initialValue)) {
        initialValue.length > 0 && initialValue.forEach(child => {
          options.push({
            key: `${initialText} - ${child}`,
            value: `${initialText} - ${child}`
          });
        });
      }
    });

    if (options && options.length > 0) {
      this.question.options = options.sort((a, b) => a.key);
      if (!this.form.get(this.question.key).value) {
        this.form.get(this.question.key).setValue(options[0].value);
      }
    }
  }

  getSelected(optValue: string) {
    const value = this.form.get(this.question.key).value;
    return value && optValue && (value === optValue) ? 'selected' : null;
  }

  get optionsPopulated(): boolean {
    if (this.question.datasource) {
      return true;
    }
    return this.question.options && this.question.options.length > 0;
  }

  get isInvalid() {
    return this.form.get(this.question.key).touched && this.form.get(this.question.key).invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ?
      this.showIf :
      true;
    return (this.question.showIfPopulated !== true || (this.value)) &&
      showIfQuestionPopulated && this.question.show !== false;
  }

  get alert() {
    return this.question.alert && this.question.alert[this.form.get(this.question.key).value];
  }

  get answerValue() {
    return this.form.get(this.question.key).value;
  }

  /**
   * Filter out question options based on the value of other form fields
   * @param CardOption[] options
   */
  private filterQuestionOptions() {
    this.currentFilter = Object.assign([], this.question.options).filter((option) => {
      if (!option.filter) {
        return true;
      }
      return option.filter.key && this.form.get(option.filter.key).value === option.filter.value;
    });
    const patchValue = this.currentFilter[this.getOptionValueIdx()].value;
    setTimeout(() => {
      this.form.get(this.question.key).patchValue(patchValue);
    }, 100);
  }

  /**
   * If there is currently a value in this dropdown then return its index within the
   * current question.options
   * @return number valueIdx
   */
  private getOptionValueIdx() {
    const currentFormValue = this.form.get(this.question.key).value;
    if (currentFormValue) {
      const idx = this.currentFilter
        .findIndex(function (filter) { return filter.value === currentFormValue; });
      return idx > -1 ? idx : 0;
    }
    return 0;
  }

  private setSubscriptions() {
    if (this.question.subscriptions) {
      this.question.subscriptions.forEach((subscription) => {
        this[subscription.trigger]();
        this.form.controls[subscription.key]
          .valueChanges
          .subscribe(() => {
            this[subscription.trigger]();
          });
      });
    }
  }
}
